import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@material-ui/core";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Redirect } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { registerFullSchema, RegisterFullFormData } from "@/validations/register";
import { SessionState } from "@/view_models/atoms";

import RegisterBasicPage from "./components/Basic";
import RegisterPhoneNumberPage from "./components/PhoneNumber";
import RegisterSmsVerificationCodePage from "./components/SmsCode";

export const RegisterPage: React.FC = () => {
  const session = useRecoilValue(SessionState);
  // 入場時だけ判定し、この画面内で登録・ログインに成功した際の完了表示は維持する。
  const loggedInOnEntry = React.useRef(session.logged_in).current;
  return loggedInOnEntry ? <Redirect to="/first_account" /> : <RegistrationFlow />;
};

const RegistrationFlow: React.FC = () => {
  const methods = useForm<RegisterFullFormData>({
    resolver: zodResolver(registerFullSchema),
    mode: "onTouched",
    shouldUnregister: false,
    defaultValues: {
      family_name: "",
      first_name: "",
      family_name_kana: "",
      first_name_kana: "",
      email: "",
      password: "",
      password_confirmation: "",
      phone_number: "",
      request_the_proposal: false,
      phone_number_auth_code: 0,
    },
  });
  const [currentStep, setCurrentStep] = React.useState(0);
  // 電話番号画面で送信済み（429）だった場合に、認証コード画面の再送カウントダウンへ残り秒数を引き継ぐ
  const [smsResendCooldownSeconds, setSmsResendCooldownSeconds] = React.useState<number | undefined>(undefined);
  const [existingCodeAvailable, setExistingCodeAvailable] = React.useState(false);

  const validate = React.useCallback(async () => {
    const fields: Array<keyof RegisterFullFormData> = currentStep === 0 ? ["email", "password"] : ["phone_number"];
    const isValid = await methods.trigger(fields);
    if (!isValid)
      window.setTimeout(() => {
        const field = document.querySelector<HTMLElement>("[data-registration-flow] [aria-invalid=\"true\"]");
        field?.scrollIntoView?.({ block: "center", behavior: "smooth" });
        field?.focus();
      }, 0);
    return isValid;
  }, [methods, currentStep]);

  React.useEffect(() => {
    // ステップが切り替わったタイミングでAdjustイベントを発火
    switch (currentStep) {
      case 0:
        sendEventToAppMeasurementTool(AppEventTokenEnum.RegistrationRegistrationAccess);
        break;
      case 1:
        sendEventToAppMeasurementTool(AppEventTokenEnum.RegistrationSmsNumberInputAccess);
        break;
      case 2:
        sendEventToAppMeasurementTool(AppEventTokenEnum.RegistrationSmsCodeInputAccess);
        break;
    }
  }, [currentStep]);

  const steps = [
    <RegisterBasicPage key="step1" onNext={() => setCurrentStep((prev) => prev + 1)} validate={validate} />,
    <RegisterPhoneNumberPage
      key="step2"
      onNext={(resendCooldownSeconds, codeAvailable = false) => {
        setSmsResendCooldownSeconds(resendCooldownSeconds);
        setExistingCodeAvailable(codeAvailable);
        setCurrentStep((prev) => prev + 1);
      }}
      onBack={() => setCurrentStep((prev) => prev - 1)}
      validate={validate}
    />,
    <RegisterSmsVerificationCodePage
      key="step3"
      initialCooldownSeconds={smsResendCooldownSeconds}
      initialCodeAvailable={existingCodeAvailable}
      onBack={() => setCurrentStep((prev) => prev - 1)}
    />,
  ];

  return (
    <FormProvider {...methods}>
      <Box>{steps[currentStep]}</Box>
    </FormProvider>
  );
};
